import React, { FC, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Row, Col } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { EyeInvisibleOutlined, EyeOutlined, CheckCircleFilled } from '@ant-design/icons';
import { setPassword } from '../../../core/user/userApi';
import { SetPasswordResponse } from '../../../core/user/types';
import './createPasswordForm.scss';
import { FinishedFailedProps, valuesProps, CreatePasswordFormProps } from './types';
import { AppDispatch, CombinedStates } from '../../../core/types';
import { checkPassword, isPasswordValid, PASSWORD_REQUIREMENTS } from '../../../core/utils/passwordPolicy';

const CreatePasswordForm:FC<CreatePasswordFormProps> = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [password, setPasswordState] = useState('');
    const {
        isFetching,
    } = useSelector((state:CombinedStates) => state.user);
    const { token, email } = useParams();
    const [form] = Form.useForm();

    const passwordStatus = checkPassword(password);

    const onFinish = (values: valuesProps) => {
        dispatch(setPassword({
            email,
            password: values.newPassword,
            passwordConfirmation: values.confirmPassword,
            token,
        }))
            .unwrap()
            .then((response: SetPasswordResponse) => {
                if (response.success) {
                    setTimeout(() => {
                        navigate('/', { replace: true });
                    }, 2000);
                } else {
                    console.error('Password creation failed:', response.message);
                }
            })
            .catch((error: unknown) => {
                console.error('Password creation failed:', error);
            });
    };

    const renderIcon = (visible:boolean) => (visible ? <EyeOutlined style={{ color: '#20639B', fontSize: '20px' }} /> : <EyeInvisibleOutlined style={{ color: '#20639B', fontSize: '20px' }} />);

    const onFinishFailed = ({ errorFields }:FinishedFailedProps) => {
        form.scrollToField(errorFields[0]?.name);
    };

    return (

        <div className="login-form-box">
            <div className="form">
                <h3 className="form-title h-1">Create Password</h3>
                <p className="sub-title">To complete the creation of your account, please enter a password you wish to use to access to your account</p>
                <Form onFinish={onFinish} onFinishFailed={onFinishFailed} layout="vertical">
                    <Form.Item label="Password"
                        name="newPassword"
                        rules={[
                            { required: true, message: 'Password is required' },
                            () => ({
                                validator(_, value) {
                                    setPasswordState(value || '');
                                    if (!value || isPasswordValid(value)) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Password does not meet requirements'));
                                },
                            }),
                        ]}>
                        <Input.Password iconRender={renderIcon} />
                    </Form.Item>
                    <div className="container-one">
                        <h3 className="password-requirements">Password must:</h3>
                        <ul className="password-criteria">
                            {PASSWORD_REQUIREMENTS.map((requirement) => (
                                <li key={requirement.key} style={{ color: passwordStatus[requirement.key] ? 'green' : 'black' }}>
                                    <CheckCircleFilled /> {requirement.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <Form.Item label="Verify Password"
                        name="confirmPassword"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }

                                    return Promise.reject(new Error('The two passwords that you entered do not match!'));
                                },
                            }),
                        ]}>
                        <Input.Password iconRender={renderIcon} />
                    </Form.Item>
                    <Row>
                        <Col xs={24}>
                            <Button size="large" block htmlType="submit" className="save-btn" loading={isFetching}>
                                Create Account
                            </Button>
                        </Col>
                    </Row>

                </Form>
            </div>
        </div>

    );
};

export default CreatePasswordForm;
